import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { RevealLine } from "./RevealLine";
import { HERO } from "@/constants/testIds";

const HERO_IMG =
  "https://images.pexels.com/photos/33345481/pexels-photo-33345481.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2200";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="hero"
      data-testid={HERO.root}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Detailed black sports car under studio lighting"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 hero-overlay" />

      {/* Meta rails */}
      <div className="absolute inset-x-0 top-24 md:top-28 z-10 px-6 md:px-12 flex justify-between eyebrow">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          ◉ Est. Paralowie · SA 5108
        </motion.span>
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="hidden md:inline"
        >
          07 Days · 08:30 — 18:30
        </motion.span>
      </div>

      {/* Content */}
      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-end px-6 md:px-12 pb-24 md:pb-32"
      >
        <div className="max-w-[1600px] mx-auto w-full">
          <div className="eyebrow mb-6 md:mb-10">
            <RevealLine delay={0.4}>Chapter 00 — Prelude</RevealLine>
          </div>

          <h1 className="font-serif font-light uppercase tracking-[-0.02em] leading-[0.9] text-white text-[clamp(3.5rem,14vw,10rem)] md:text-[clamp(4rem,10.5vw,10rem)] lg:text-[clamp(4rem,9.2vw,10rem)]">
            <RevealLine delay={0.5}>Where</RevealLine>
            <RevealLine delay={0.65} className="text-brand-red">
              perfection
            </RevealLine>
            <RevealLine delay={0.8} className="italic text-neutral-300">
              meets the road.
            </RevealLine>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <p className="md:col-span-5 font-sans text-base md:text-lg font-light text-neutral-300 leading-relaxed max-w-md">
              <RevealLine delay={1.0}>
                A meticulous, all-in car detailing studio
              </RevealLine>
              <RevealLine delay={1.1}>
                treating each vehicle as a singular object of care.
              </RevealLine>
            </p>

            <div className="md:col-span-4 md:col-start-9 flex flex-col sm:flex-row gap-3">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.7 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => scrollTo("booking")}
                data-testid={HERO.cta}
                className="btn-primary"
              >
                Book a Detail
                <ArrowDown size={14} className="-rotate-45" />
              </motion.button>
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.7 }}
                href="tel:0429052189"
                data-testid={HERO.callBtn}
                className="btn-ghost"
              >
                <Phone size={14} />
                0429 052 189
              </motion.a>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 eyebrow flex flex-col items-center gap-3"
      >
        <span>Scroll</span>
        <motion.span
          className="block w-px h-8 bg-white/40"
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
      </motion.div>
    </section>
  );
}
